import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"
import { Tooltip } from "bootstrap"
import { isAbortError } from "../utils/fetch-error.util"

const SEARCH_DEBOUNCE_MS = 180
const RECENT_ITEMS_KEY_PREFIX = "lucanto.spotlight.recent-items"
const RECENT_ITEMS_LIMIT = 6

// Fallbacks for the server-side copy passed in through the `translations`
// value; a key the view did not send never reads as `undefined`.
const DEFAULT_TRANSLATIONS = {
  emptyMessage: "",
  searchEmptyMessage: "",
  actionPlaceholder: "",
  searchPlaceholder: "",
  alreadyOpenLabel: "",
  recentGroupLabel: ""
}

export default class extends Controller {
  static targets = ["trigger", "searchTrigger", "overlay", "panel", "query", "results", "status", "liveRegion"]

  static values = {
    translations: { type: Object, default: {} },
    searchUrl: String,
    workspaceId: String
  }

  get #texts() {
    return { ...DEFAULT_TRANSLATIONS, ...this.translationsValue }
  }

  connect() {
    this.isOpen = false
    this.mode = "action"
    this.selectedIndex = 0
    this.searchDebounce = null
    this.abortController = null
    this.requestId = 0
    this.lastQuery = ""
    this.items = []
    this.resultsHtml = ""
    this.recentItems = this.loadRecentItems()
    this.lastFocusedElement = null
    this.statusTimeout = null

    this.handleGlobalKeydown = this.handleGlobalKeydown.bind(this)
    this.handleDocumentClick = this.handleDocumentClick.bind(this)

    document.addEventListener("keydown", this.handleGlobalKeydown)
    document.addEventListener("click", this.handleDocumentClick)

    this.overlayTarget.setAttribute("aria-hidden", "true")
  }

  disconnect() {
    document.removeEventListener("keydown", this.handleGlobalKeydown)
    document.removeEventListener("click", this.handleDocumentClick)
    this.cancelSearch()
    clearTimeout(this.statusTimeout)
    this.unlockBodyScroll()
  }

  toggle(event) {
    event.preventDefault()

    if (this.isOpen) {
      this.indicateAlreadyOpen()
      return
    }

    this.open("action")
  }

  openSearch(event) {
    event.preventDefault()

    if (this.isOpen) {
      this.mode = "search"
      this.updateInputPlaceholder()
      const query = this.queryTarget.value.trim()
      if (query === "") {
        this.renderRecentResults()
      } else {
        this.loadResults(query)
      }
      requestAnimationFrame(() => this.queryTarget.focus())
      return
    }

    this.open("search")
  }

  closeByOverlay(event) {
    if (event.target === this.overlayTarget) {
      this.close()
    }
  }

  onQueryInput() {
    if (this.mode === "search" && this.queryTarget.value.trim() === "") {
      this.cancelSearch()
      this.lastQuery = ""
      this.renderRecentResults()
      return
    }

    this.scheduleSearch()
  }

  onInputKeydown(event) {
    if (event.key === "ArrowDown") {
      event.preventDefault()
      this.selectNext()
      return
    }

    if (event.key === "ArrowUp") {
      event.preventDefault()
      this.selectPrevious()
      return
    }

    if (event.key === "Enter") {
      event.preventDefault()
      this.activateSelection()
      return
    }

    if (event.key === "Home") {
      event.preventDefault()
      this.selectedIndex = 0
      this.updateSelectionVisuals()
      return
    }

    if (event.key === "End") {
      event.preventDefault()
      this.selectedIndex = Math.max(0, this.items.length - 1)
      this.updateSelectionVisuals()
      return
    }

    if (event.key === "Escape") {
      event.preventDefault()
      this.close()
      return
    }

    this.handleNumberShortcut(event)
  }

  activateSelection() {
    const selectedItem = this.items[this.selectedIndex]
    if (!selectedItem?.url) {
      return
    }

    this.trackRecentItem(selectedItem)
    this.close()
    Turbo.visit(selectedItem.url)
  }

  open(mode = "action") {
    this.isOpen = true
    this.mode = mode
    this.lastFocusedElement = document.activeElement
    this.hideSearchTooltip()

    this.overlayTarget.classList.add("is-open")
    this.overlayTarget.setAttribute("aria-hidden", "false")
    document.body.classList.add("spotlight-open")

    this.queryTarget.value = ""
    this.updateInputPlaceholder()
    this.selectedIndex = 0

    if (mode === "search") {
      this.renderRecentResults()
    } else {
      this.loadResults("")
    }

    requestAnimationFrame(() => {
      this.queryTarget.focus()
      this.queryTarget.select()
    })
  }

  close() {
    if (!this.isOpen) {
      return
    }

    this.isOpen = false
    this.overlayTarget.classList.remove("is-open")
    this.overlayTarget.setAttribute("aria-hidden", "true")
    this.unlockBodyScroll()
    this.cancelSearch()
    this.hideSearchTooltip()

    // Blur the query input first — if focus restoration below fails silently,
    // the input stays focused (though invisible), blocking keyboard shortcuts.
    this.queryTarget.blur()

    if (this.lastFocusedElement?.focus) {
      const isSearchTriggerFocused = this.hasSearchTriggerTarget && this.lastFocusedElement === this.searchTriggerTarget
      if (isSearchTriggerFocused && this.hasTriggerTarget) {
        this.triggerTarget.focus()
      } else {
        this.lastFocusedElement.focus()
      }
    }
  }

  handleGlobalKeydown(event) {
    // Browser extensions and injected third-party scripts dispatch plain Events
    // named "keydown" on document. Those are not KeyboardEvents: `key` is absent
    // and every modifier reads `undefined`, so a `code`-only synthetic event would
    // otherwise satisfy the "/" branch below and open the panel unprompted. A real
    // keypress always carries a string `key`, so requiring one rejects them. LUC-713.
    if (typeof event.key !== "string") return

    const isPlainNShortcut = !event.metaKey && !event.ctrlKey && !event.altKey && event.key.toLowerCase() === "n"
    // `event.key` only — never `event.code`. On the SK/CS QWERTZ layout the
    // physical Slash key produces "-", so the `code` fallback opened Spotlight
    // every time a Slovak or Czech user typed a dash outside a field.
    const isSlashShortcut = !event.metaKey && !event.ctrlKey && !event.altKey && event.key === "/"
    const isCommandSearch = (event.metaKey || event.ctrlKey) && event.shiftKey && event.key.toLowerCase() === "k"
    const isQuickSearch = (event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k"

    if (!this.isOpen && isPlainNShortcut && !this.isEditableTarget(event.target)) {
      event.preventDefault()
      this.open("action")
      return
    }

    if (!this.isOpen && isSlashShortcut && !this.isEditableTarget(event.target)) {
      event.preventDefault()
      this.open("search")
      return
    }

    if (isCommandSearch || isQuickSearch) {
      event.preventDefault()
      if (this.isOpen) {
        this.indicateAlreadyOpen()
      } else {
        this.open("search")
      }
      return
    }

    if (!this.isOpen) {
      return
    }

    if (event.key === "Escape") {
      event.preventDefault()
      this.close()
      return
    }

    this.handleNumberShortcut(event)
  }

  handleDocumentClick(event) {
    if (!this.isOpen) {
      return
    }

    const clickedTrigger = this.triggerTargets.some((trigger) => trigger.contains(event.target))
    const clickedSearchTrigger = this.hasSearchTriggerTarget && this.searchTriggerTarget.contains(event.target)
    if (this.panelTarget.contains(event.target) || clickedTrigger || clickedSearchTrigger) {
      return
    }

    this.close()
  }

  handleNumberShortcut(event) {
    if (event.metaKey || event.ctrlKey || event.altKey) {
      return
    }

    const number = Number.parseInt(event.key, 10)
    if (!number || number < 1 || number > 9) {
      return
    }

    const actionIndex = this.items.findIndex((item) => item.kind === "action" && item.number === number)
    if (actionIndex < 0) {
      return
    }

    event.preventDefault()
    this.selectedIndex = actionIndex
    this.updateSelectionVisuals()
    this.activateSelection()
  }

  scheduleSearch() {
    clearTimeout(this.searchDebounce)
    this.searchDebounce = setTimeout(() => {
      this.loadResults(this.queryTarget.value.trim())
    }, SEARCH_DEBOUNCE_MS)
  }

  async loadResults(query) {
    this.cancelSearch()
    this.lastQuery = query
    this.abortController = new AbortController()
    const currentRequestId = ++this.requestId

    try {
      const response = await fetch(`${this.searchUrlValue}?q=${encodeURIComponent(query)}`, {
        headers: { Accept: "application/json" },
        signal: this.abortController.signal
      })

      if (this.requestId !== currentRequestId) return

      if (!response.ok) {
        throw new Error(`Spotlight search failed: ${response.status}`)
      }

      const payload = await response.json()

      if (this.requestId !== currentRequestId) return

      this.resultsHtml = payload.results_html || ""
      this.renderResultsFromServer(query)
    } catch (error) {
      if (isAbortError(error)) {
        return
      }

      this.resultsHtml = ""
      this.items = []
      this.selectedIndex = 0
      this.renderErrorState()
    }
  }

  cancelSearch() {
    clearTimeout(this.searchDebounce)
    if (this.abortController) {
      this.abortController.abort()
      this.abortController = null
    }
  }

  selectNext() {
    if (!this.items.length) return

    this.selectedIndex = (this.selectedIndex + 1) % this.items.length
    this.updateSelectionVisuals()
  }

  selectPrevious() {
    if (!this.items.length) return

    this.selectedIndex = (this.selectedIndex - 1 + this.items.length) % this.items.length
    this.updateSelectionVisuals()
  }

  indicateAlreadyOpen() {
    this.panelTarget.classList.remove("spotlight__panel--pulse")
    // Force reflow to restart animation.
    void this.panelTarget.offsetWidth
    this.panelTarget.classList.add("spotlight__panel--pulse")

    this.liveRegionTarget.textContent = this.#texts.alreadyOpenLabel
    this.flashStatus(this.#texts.alreadyOpenLabel)
  }

  unlockBodyScroll() {
    document.body.classList.remove("spotlight-open")
  }

  renderResultsFromServer(query) {
    this.lastQuery = query
    this.resultsTarget.innerHTML =
      this.resultsHtml || `<div class="spotlight__empty">${this.escapeHtml(this.#texts.emptyMessage)}</div>`
    this.bindItemSelection()
    this.selectedIndex = this.defaultSelectionIndex(query)
    this.updateSelectionVisuals()
  }

  renderSection(title, items) {
    const itemMarkup = items
      .map((item) => {
        const shortcut =
          item.kind === "action" && item.number ? `<span class="spotlight__shortcut">${item.number}</span>` : ""

        return `
          <button
            type="button"
            class="spotlight__item"
            data-id="${this.escapeHtml(item.id || "")}"
            data-kind="${this.escapeHtml(item.kind || "recent")}"
            data-url="${this.escapeHtml(item.url || "")}"
            data-category="${this.escapeHtml(item.category || "invoices")}"
            ${item.number ? `data-number="${item.number}"` : ""}
          >
            <span class="material-symbols-outlined spotlight__item-icon">${this.escapeHtml(item.icon || "description")}</span>
            <span class="spotlight__item-content">
              <span class="spotlight__item-title">${this.escapeHtml(item.title || "")}</span>
            </span>
            ${shortcut}
          </button>
        `
      })
      .join("")

    return `
      <section class="spotlight__section">
        <h3 class="spotlight__section-title">${this.escapeHtml(title)}</h3>
        <div class="spotlight__section-items">
          ${itemMarkup}
        </div>
      </section>
    `
  }

  renderRecentResults() {
    const visibleRecentItems = this.recentItems || []

    if (!visibleRecentItems.length) {
      this.resultsTarget.innerHTML = `<div class="spotlight__empty">${this.escapeHtml(this.#texts.searchEmptyMessage)}</div>`
      this.items = []
      this.selectedIndex = 0
      return
    }

    this.resultsTarget.innerHTML = this.renderSection(this.#texts.recentGroupLabel, visibleRecentItems)
    this.bindItemSelection()
    this.selectedIndex = 0
    this.updateSelectionVisuals()
  }

  bindItemSelection() {
    this.hydrateItemsFromDom()

    this.resultsTarget.querySelectorAll(".spotlight__item").forEach((node) => {
      node.addEventListener("mouseenter", () => {
        const index = Number.parseInt(node.dataset.index || "0", 10)
        this.selectedIndex = index
        this.updateSelectionVisuals()
      })

      node.addEventListener("click", () => {
        const index = Number.parseInt(node.dataset.index || "0", 10)
        this.selectedIndex = index
        this.updateSelectionVisuals()
        this.activateSelection()
      })
    })
  }

  renderErrorState() {
    this.resultsTarget.innerHTML = `<div class="spotlight__empty">${this.escapeHtml(this.#texts.emptyMessage)}</div>`
  }

  flashStatus(message) {
    if (!this.hasStatusTarget) {
      return
    }

    clearTimeout(this.statusTimeout)
    this.statusTarget.textContent = message
    this.statusTarget.classList.add("is-visible")

    this.statusTimeout = setTimeout(() => {
      this.statusTarget.classList.remove("is-visible")
    }, 1300)
  }

  defaultSelectionIndex(query) {
    if (!this.items.length) {
      return 0
    }

    if (this.mode === "search" && query === "") {
      return 0
    }

    if (query !== "") {
      return 0
    }

    const createInvoiceIndex = this.items.findIndex((item) => item.id === "create-invoice")
    return createInvoiceIndex >= 0 ? createInvoiceIndex : 0
  }

  updateSelectionVisuals() {
    const nodes = this.resultsTarget.querySelectorAll(".spotlight__item")
    nodes.forEach((node) => {
      const index = Number.parseInt(node.dataset.index || "-1", 10)
      node.classList.toggle("is-active", index === this.selectedIndex)
    })

    const selectedNode = this.resultsTarget.querySelector(`.spotlight__item[data-index="${this.selectedIndex}"]`)
    selectedNode?.scrollIntoView?.({ block: "nearest" })
  }

  updateInputPlaceholder() {
    this.queryTarget.placeholder =
      this.mode === "search" ? this.#texts.searchPlaceholder : this.#texts.actionPlaceholder
  }

  get recentItemsStorageKey() {
    return `${RECENT_ITEMS_KEY_PREFIX}.${this.workspaceIdValue}`
  }

  loadRecentItems() {
    try {
      const payload = window.localStorage?.getItem(this.recentItemsStorageKey)
      if (!payload) return []

      const parsed = JSON.parse(payload)
      if (!Array.isArray(parsed)) return []

      return parsed.filter((item) => item?.id && item.title && item.url).slice(0, RECENT_ITEMS_LIMIT)
    } catch {
      return []
    }
  }

  storeRecentItems() {
    try {
      window.localStorage?.setItem(
        this.recentItemsStorageKey,
        JSON.stringify(this.recentItems.slice(0, RECENT_ITEMS_LIMIT))
      )
    } catch {
      // Ignore local storage failures in private mode or locked environments.
    }
  }

  trackRecentItem(item) {
    if (!item?.id || !item?.title || !item?.url) return

    const recentItem = {
      id: item.id,
      title: item.title,
      url: item.url,
      icon: item.icon || "description",
      kind: item.kind || "recent",
      category: item.category || "invoices",
      number: item.number || null
    }

    const withoutCurrent = this.recentItems.filter((existingItem) => existingItem.id !== recentItem.id)
    this.recentItems = [recentItem, ...withoutCurrent].slice(0, RECENT_ITEMS_LIMIT)
    this.storeRecentItems()
  }

  hideSearchTooltip() {
    if (!this.hasSearchTriggerTarget) {
      return
    }

    const tooltip = Tooltip.getInstance(this.searchTriggerTarget)
    tooltip?.hide()
    this.searchTriggerTarget.blur()
  }

  hydrateItemsFromDom() {
    const nodes = this.resultsTarget.querySelectorAll(".spotlight__item")

    this.items = Array.from(nodes).map((node, index) => {
      node.dataset.index = index.toString()
      const number = Number.parseInt(node.dataset.number || "", 10)

      return {
        id: node.dataset.id || "",
        title: node.querySelector(".spotlight__item-title")?.textContent?.trim() || "",
        url: node.dataset.url || "",
        icon: node.querySelector(".spotlight__item-icon")?.textContent?.trim() || "description",
        kind: node.dataset.kind || "action",
        category: node.dataset.category || "invoices",
        number: Number.isNaN(number) ? null : number
      }
    })
  }

  escapeHtml(value) {
    return value
      .toString()
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#39;")
  }

  isEditableTarget(target) {
    if (!target || !(target instanceof Element)) {
      return false
    }

    if (target.closest("input, textarea, select, [contenteditable='true']")) {
      return true
    }

    return target.isContentEditable
  }
}
